/**
 * permission-areas — podział katalogu uprawnień na obszary Centrum (F24 Kawałek 1).
 *
 * Centrum ma cztery zakładki, a katalog ~200 kodów. Trzy z tych zakładek biorą kody stąd:
 *   • KARTOTEKI — kody objęte drzewem obiektów (dokumenty, kontrahenci, pracownicy, towary).
 *     Nie wypisujemy ich na liście płaskiej, bo nadaje się je z hierarchią; zbiór wyliczamy z
 *     samego drzewa, więc nie może się rozjechać z tym, co pokazuje tamta zakładka.
 *   • ADMINISTRACYJNE — konfiguracja systemu: kto może zmieniać ustawienia, słowniki, role,
 *     moduły, integracje. Prawa, które zwykły pracownik dostaje wyjątkowo.
 *   • OGÓLNE — cała reszta: codzienna praca, której nie da się przypiąć do kartoteki (procesy,
 *     pulpit, widgety, magazyny).
 *
 * Podział obszaru administracyjnego trzymamy w JEDNYM miejscu — liście kategorii poniżej —
 * właśnie po to, żeby przesunięcie obszaru między zakładkami było zmianą jednej linijki, a nie
 * polowaniem po komponentach. Flaga `isAdminOnly` z katalogu bierze udział w decyzji, ale sama
 * nie wystarcza: ma ją tylko garstka kodów, a „ustawienia firmy" czy „słowniki" są równie
 * administracyjne, mimo że flagi nie mają.
 */

/** Kategorie katalogu uznane za konfigurację systemu. */
const ADMIN_CATEGORIES = new Set([
  'roles',
  'users',
  'companies',
  'dictionaries',
  'document_types',
  'menu_layouts',
  'system_modules',
  'custom_modules',
  'widgets',
  'chart_of_accounts',
  'record_procedures',
  'non_working_days',
  'processes_procedures',
  'processes_templates',
  'processes_definitions',
  'symfonia_integration',
  'ksef_integration',
]);

export type PermissionArea = 'ownRecords' | 'modules' | 'general' | 'admin';

export interface CatalogDefinition {
  code: string;
  moduleCode: string;
  name: string | null;
  description: string | null;
  category: string | null;
  isAdminOnly: boolean;
}

/**
 * Prawa do REKORDÓW modułu własnego (`crm_core.module.<moduł>.view|edit|delete`). Katalog trzyma
 * je w kategorii `custom_modules` razem z prawem do budowania modułów, ale to codzienna praca —
 * ktoś wpisuje badania lekarskie albo wypadki, nie konfiguruje systemu.
 *
 * Trafiają do zakładki KARTOTEKI WŁASNE, nie do „Modułów": moduł własny jest w istocie kartoteką
 * zbudowaną przez klienta — ma własną tabelę, kategorie i formatkę, a prawa nadaje się do jego
 * REKORDÓW, dokładnie jak przy kontrahentach czy dokumentach. Zakładka „Moduły" zostaje dla tego,
 * co kartoteką nie jest (Handel i magazyn, Magazyny, Procesy).
 */
const MODULE_RECORD_PREFIX = 'crm_core.module.';

/**
 * Moduły dokupowane, które NIE są kartoteką — mają własną logikę, a nie zbiór rekordów
 * z formatką. Rozpoznajemy je po module katalogu, bo to jedyna rzecz, która je łączy.
 *
 * Integracje (Symfonia, KSeF) świadomie tu NIE wchodzą — to konfiguracja systemu i zostają
 * w uprawnieniach administracyjnych.
 */
const STANDALONE_MODULE_CODES = new Set(['handel_magazyn', 'warehouses', 'processes']);

/** Slug modułu wyjęty z kodu: `crm_core.module.bhp_wypadki.view` → `bhp_wypadki`. */
export function moduleSlugOf(code: string): string | null {
  if (!code.startsWith(MODULE_RECORD_PREFIX)) return null;
  const rest = code.slice(MODULE_RECORD_PREFIX.length);
  const dot = rest.lastIndexOf('.');
  return dot === -1 ? rest : rest.slice(0, dot);
}

/**
 * Przedrostek klucza kartoteki modułu w drzewie — lustro stałej serwerowej z
 * `lib/permissions/object-map`. Panel nie ma dostępu do kodu serwera, a klucz przychodzi
 * w drzewie jako zwykły napis.
 */
const CUSTOM_KARTOTEKA_PREFIX = 'custom:';

/**
 * Czy węzeł drzewa należy do zakładki KARTOTEKI WŁASNE: kartoteka modułu utworzona przez
 * tenanta. Kartoteka modułu zainstalowana przez ITE (`installedByIte`) jest dla tenanta
 * systemowa — zostaje w zakładce „Kartoteki" razem z kontrahentami czy dokumentami.
 */
export function isOwnKartotekaNode(node: { key: string; installedByIte?: boolean }): boolean {
  return node.key.startsWith(CUSTOM_KARTOTEKA_PREFIX) && !node.installedByIte;
}

/** Do której zakładki trafia kod spoza drzewa kartotek. */
export function areaOf(def: CatalogDefinition): PermissionArea {
  if (def.code.startsWith(MODULE_RECORD_PREFIX)) return 'ownRecords';
  if (def.isAdminOnly) return 'admin';
  if (def.category != null && ADMIN_CATEGORIES.has(def.category)) return 'admin';
  // Moduł dokupowany sprawdzamy PO obszarach administracyjnych: konfiguracja procesów
  // (definicje, szablony, procedury) zostaje przy administracji, a przy module lądują jego
  // sprawy codzienne.
  if (STANDALONE_MODULE_CODES.has(def.moduleCode)) return 'modules';
  return 'general';
}
